class gTagEvents {
    constructor() {
        this.delegateBody = new Delegate(document.body);
        
        this.bindEvents();
        this.observeDOMChanges();
        this.handleIntersects();
        this.bindSubscriptionDropdownTracking();
        this.bindStickyAtcCapture();

        this.interceptCartApi();
    }

    /**
     * Wires every delegated dataLayer listener onto document.body.
     *
     * @returns {void}
     */
    bindEvents() {
        this.delegateBody.on("click", "button[name='checkout']", this.handleCheckout.bind(this));
        this.delegateBody.on("click", '[data-action="ga-send-select"]', this.handleSelectItem.bind(this));
        this.delegateBody.on("click", ".swiper-zoom-target", this.handleProductImageZoomTargetClick.bind(this));
        this.delegateBody.on('variant:changed:gtm', this.handleVariantChange.bind(this));
        this.delegateBody.on("click", 'button[data-action="show-more"]', this.handleShowMoreToggleClick.bind(this));
        this.delegateBody.on("click", ".Dropdown--ButtonPDP", this.handlePdpDropdownClick.bind(this));

        // Capture phase so Swiper / mobile-tap handlers can't swallow the click.
        // Delegate passes the matched element as the second argument to the handler.
        const recSelector = [
            "[data-gtm-rec] .cardWrapper--Link",
            ".CustomCart__Recommendations .cardWrapper--Link",
            ".searchInitial--Products .cardWrapper--Link",
        ].join(", ");
        this.delegateBody.on("click", recSelector, this.handleRecommendedProductClick.bind(this), true);

        // Every navigation block marks itself in Liquid with data-gtm-block-title, which is where
        // block_title is read from: the <shop-by> tiles, both collection link bars (the grid block
        // and the standalone section) and the breadcrumb trail. Each block is ALSO named here by
        // its own markup, because the marker and this file ship as separate theme files: a theme
        // serving a stale Liquid file would otherwise drop the event for that block entirely
        // rather than just reporting it with an empty block_title.
        // Capture phase: on collection pages <shop-by> intercepts these clicks and navigates
        // asynchronously, so read the current URL before it is replaced.
        const navSelector = [
            "[data-gtm-block-title] a",
            "shop-by a",
            ".CollectionGrid--Links a",
            "nav.breadcrumbs a",
        ].join(", ");
        this.delegateBody.on("click", navSelector, this.handleCollectionsNavigationClick.bind(this), true);

        // The filter form, the sort list and the load more button are all shared with the search
        // template, which renders the same markup from main-search.liquid, so binding them
        // unconditionally would report search filtering and sorting as collection activity with
        // nothing in the payload to tell the two apart. main-collection-grid is rendered by the two
        // collection grid sections and by nothing else, so its presence is what scopes the
        // listeners to the collection page. This script is deferred, so the element is parsed by
        // the time the constructor runs.
        if (document.querySelector("main-collection-grid")) {
            this.bindCollectionEvents();
        }
    }

    /**
     * Wires the collection page listeners. Called only on templates that render the collection
     * grid, so none of these events can be raised by the search results page.
     *
     * @returns {void}
     */
    bindCollectionEvents() {
        // Filters only take effect on "Apply" - a checkbox change just re-renders the
        // filter panel. Capture phase so the form is read before it is re-rendered.
        this.delegateBody.on("click", 'collection-filters-form [data-action="submit-filter"]', this.handleCollectionsFilterApplied.bind(this), true);
        this.delegateBody.on("click", 'collection-filters-form [data-action="clear-filter"]', this.handleCollectionsFilterRemoved.bind(this), true);
        // Sort is committed by the radio inside the option, matching the theme's own binding.
        this.delegateBody.on("change", 'collection-filters-form [data-action="apply-sort"]', this.handleCollectionsSortApplied.bind(this), true);

        // "Apply" and each sort option are div[tabindex="0"], not buttons, so Enter and Space raise
        // no click of their own: the theme commits both from its own keydown handler in
        // collection-filters-form.js, and for sort it ticks the radio programmatically, which fires
        // no change event either. Without these two the keyboard path is silently untracked while
        // the mouse path looks healthy. "clear-filter" needs no equivalent - it renders as a real
        // anchor, so Enter already produces the click the listener above is waiting for.
        this.delegateBody.on("keydown", 'collection-filters-form [data-action="submit-filter"]', this.handleCollectionsFilterApplied.bind(this), true);
        this.delegateBody.on("keydown", 'collection-filters-form [data-action="apply-sort"]', this.handleCollectionsSortApplied.bind(this), true);

        this.delegateBody.on("click", "main-collection-grid .cardWrapper--Link", this.handleCollectionsProductClick.bind(this), true);
        // The visible "Load more" button is the #infiniteScroll div; #paginateNext is only the link
        // inside it, so binding to the link misses every click on the button's padding. The theme
        // itself listens on #infiniteScroll (global.js), so this matches exactly what loads a page.
        this.delegateBody.on("click", "#infiniteScroll", this.handleCollectionsLoadMore.bind(this), true);
        // The block holding the "see all" link is marked in Liquid rather than matched through the
        // class names of its merchant-authored rich text, so the event and the thing it measures
        // cannot drift apart when those presentational classes are renamed.
        this.delegateBody.on("click", "main-collection-grid [data-gtm-see-all] a", this.handleCollectionsSeeAllClick.bind(this), true);
        this.delegateBody.on("click", '[data-action="usp-banner"]', this.handleCollectionsBannerClick.bind(this), true);
    }

    /**
     * Reports a click on a linked banner block on a collection page.
     *
     * @param {MouseEvent} event - The click event.
     * @param {HTMLAnchorElement} link - The banner block the click was delegated from.
     * @returns {void}
     */
    handleCollectionsBannerClick(event, link) {
        // The block only renders an href when the merchant set a link on it.
        if (!link.href) return;

        this.pushDataLayerEvent({
            event: "collections_banner_click",
            destination_url: link.href,
            // A collection page can carry several banners, so the block identifies itself: the
            // category is the merchant's own heading and is what makes the report readable, the id
            // is stable across copy changes. The category is empty on a block with no heading set.
            collections_banner_category: this.normalizeGtmText(link.dataset.gtmBanner),
            banner_id: link.dataset.gtmBannerId || "",
        });
    }

    /**
     * Reports a click on the "see all" link of a collection grid heading block.
     *
     * @param {MouseEvent} event - The click event.
     * @param {HTMLAnchorElement} link - The link that was clicked.
     * @returns {void}
     */
    handleCollectionsSeeAllClick(event, link) {
        const block = link.closest("[data-gtm-see-all]");

        this.pushDataLayerEvent({
            event: "collections_see_all_hyperlink",
            // The group the link belongs to, e.g. "Boxer Trunk". In practice the merchant leaves
            // the block heading empty and puts the group name in the preceding block's rich text,
            // so the link's own title attribute is the only per-link source and is what the
            // templates actually carry. The heading still wins when one is set.
            collections_see_all_category:
                this.normalizeGtmText(block?.dataset.gtmSeeAll) || this.normalizeGtmText(link.title),
            destination_url: link.href || "",
        });
    }

    /**
     * Reports the shopper clearing every active filter. Carries no payload: "Clear all" says
     * nothing about which filters were dropped, and the set is gone by the time it is read.
     *
     * @returns {void}
     */
    handleCollectionsFilterRemoved() {
        this.pushDataLayerEvent({ event: "collections_filter_removed" });
    }

    /**
     * Reports the sort order the shopper committed, from either the mouse or the keyboard.
     *
     * @param {MouseEvent|KeyboardEvent} event - The change or keydown event that committed the sort.
     * @param {HTMLElement} target - The sort option the event was delegated from.
     * @returns {void}
     */
    handleCollectionsSortApplied(event, target) {
        if (!this.isGtmActivation(event)) return;

        // The option's own value attribute is read before the radio because this runs in the
        // capture phase, ahead of the theme handler that ticks the radio on a keydown: on that
        // path the radio has not been checked yet, while the attribute is right either way.
        const sortBy = this.normalizeGtmText(
            target.getAttribute("value") || target.querySelector('input[name="sort_by"]')?.value
        );
        if (!sortBy) return;

        this.pushDataLayerEvent({ event: "collections_sort_applied", sort_by: sortBy });
    }

    /**
     * Reports a click on a product card in the collection grid.
     *
     * @param {MouseEvent} event - The click event.
     * @param {HTMLAnchorElement} link - The product card link that was clicked.
     * @returns {void}
     */
    handleCollectionsProductClick(event, link) {
        // Shopify product URLs are /products/<handle>, optionally behind a locale
        // and/or collection prefix.
        const handle = link.pathname.split("/products/")[1]?.split("/")[0] || "";

        this.pushDataLayerEvent({
            event: "collections_product_click",
            product_handle: handle,
        });
    }

    /**
     * Reports the shopper loading the next page of the collection grid.
     *
     * @param {MouseEvent} event - The click event.
     * @param {HTMLElement} button - The #infiniteScroll button the click was delegated from.
     * @returns {void}
     */
    handleCollectionsLoadMore(event, button) {
        // Same guards as the theme's own handler: no next-page link means nothing loads, and a
        // click while a page is already loading is ignored by the theme, so it is not reported.
        if (!button.querySelector("a") || button.classList.contains("is-loading")) return;

        this.pushDataLayerEvent({ event: "collections_load_more" });
    }

    /**
     * Reports the filters the shopper committed, from either the mouse or the keyboard. The
     * selected filters are read off the form before the theme re-renders it.
     *
     * @param {MouseEvent|KeyboardEvent} event - The click or keydown event that applied the filters.
     * @param {HTMLElement} target - The "Apply" control the event was delegated from.
     * @returns {void}
     */
    handleCollectionsFilterApplied(event, target) {
        if (!this.isGtmActivation(event)) return;

        const form = target.closest("form");
        if (!form) return;

        const filters = this.getActiveCollectionFilters(form);
        // Applying with nothing selected is not a filter interaction.
        if (!filters.length) return;

        this.pushDataLayerEvent({
            event: "collections_filter_applied",
            // One dimension per the spec, each filter as "Name: value", e.g. "Size: M, Colour: Black".
            filter_by: filters.map((filter) => `${filter.name}: ${filter.value}`).join(", "),
        });
    }

    /**
     * Reports a click on a collection navigation item: an Explore by style/material tile inside
     * <shop-by>, a link in either of the collection link bars, or a breadcrumb.
     *
     * @param {MouseEvent} event - The click event.
     * @param {HTMLAnchorElement} link - The link that was clicked.
     * @returns {void}
     */
    handleCollectionsNavigationClick(event, link) {
        if (!link) return;
        // The breadcrumb journey ships <template> stubs with an empty href that JS fills in later,
        // so an anchor without a destination is not a navigation.
        if (!link.getAttribute("href")) return;

        const slide = link.closest(".swiper-slide");

        const payload = {
            event: "collections_navigation_click",
            block_title: this.getCollectionsNavigationBlockTitle(link),
            item_title: this.getCollectionsNavigationItemTitle(link, slide),
            item_position: this.getCollectionsNavigationItemPosition(link),
            source_url: window.location.href,
            destination_url: link.href || "",
        };

        this.pushDataLayerEvent(payload);
    }

    getCollectionsNavigationBlockTitle(link) {
        // Set on <shop-by> from its heading, and on the collection link bar as "Collection links".
        const block = link.closest("[data-gtm-block-title]");
        return this.normalizeGtmText(block?.dataset.gtmBlockTitle);
    }

    getCollectionsNavigationItemTitle(link, slide) {
        const explicit = this.normalizeGtmText(slide?.dataset.gtmItemTitle);
        if (explicit) return explicit;

        const heading = slide?.querySelector(".shop-by__collection-heading");
        return this.normalizeGtmText(heading?.textContent || link.textContent);
    }

    /**
     * Reports which place in its own navigation block the clicked item occupies, 1-based.
     *
     * @param {HTMLAnchorElement} link - The link that was clicked.
     * @returns {number|string} The 1-based position, or "" when the item sits in no list.
     */
    getCollectionsNavigationItemPosition(link) {
        // Tiles and link bars lay their items out as swiper slides, the breadcrumb trail as list
        // items, so index the clicked item among whichever of the two its own list holds. The
        // breadcrumb list also holds the separators between the items, hence the filter.
        const item = link.closest(".swiper-slide, li");
        if (!item) return "";

        const siblings = Array.from(item.parentElement?.children || [])
            .filter((sibling) => sibling.matches(".swiper-slide, li"));

        const index = siblings.indexOf(item);
        return index < 0 ? "" : index + 1;
    }

    handleRecommendedProductClick(event, link) {
        if (!link) return;

        let eventName;

        // Drawer / search have dedicated containers. Everything else relies on
        // data-gtm-rec set by the recommendations section for that page type
        // (product_recommended on PDP, cart_recommended on cart page).
        if (link.closest(".CustomCart__Recommendations")) {
            eventName = "cart_recommended";
        } else if (link.closest(".searchInitial--Products")) {
            eventName = "search_recommended";
        } else {
            const container = link.closest("[data-gtm-rec]");
            if (!container) return;
            eventName = container.getAttribute("data-gtm-rec");
        }

        if (!eventName) return;

        // Deduplicate: Swiper / nested handlers can surface the same click twice
        const card = link.closest(".productCard");
        const dedupeKey = `${eventName}:${card?.dataset?.id || link.href}`;
        const now = Date.now();
        if (
            this._lastRecClick &&
            this._lastRecClick.key === dedupeKey &&
            now - this._lastRecClick.at < 500
        ) {
            return;
        }
        this._lastRecClick = { key: dedupeKey, at: now };

        const listMap = {
            product_recommended: { id: "product_recommendations", name: "Product Recommendations" },
            cart_recommended:    { id: "cart_recommendations",    name: "Cart Recommendations" },
            search_recommended:  { id: "search_recommendations",  name: "Search Recommendations" },
        };
        const list = listMap[eventName] ?? { id: eventName, name: eventName };

        let product = {};
        try { product = JSON.parse(card?.dataset?.gtmProduct || "{}"); } catch (_) {}

        const item = {
            item_id:        product.sku    || String(product.id || ""),
            item_name:      product.title  || "",
            item_brand:     product.brand  || "",
            price:          product.price  ?? 0,
            item_list_id:   list.id,
            item_list_name: list.name,
            quantity:       1,
        };

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
            event: eventName,
            ecommerce: {
                item_list_id:   list.id,
                item_list_name: list.name,
                currency:       ShopifyAnalytics?.meta?.currency || "",
                items: [item],
            },
        });
        console.log(`dataLayer push -> ${eventName}:`, item);
    }

    handlePdpDropdownClick(event) {
        const button = event.target.closest(".Dropdown--ButtonPDP");
        if (!button) return;

        const titleRaw = (button.getAttribute("data-title") || "").trim();
        if (!titleRaw) return;

        const title = titleRaw.toLowerCase();
        let eventName = null;

        if (title === "product care") eventName = "product_product-care-info";
        else if (title === "shipping") eventName = "product_shipping-info";
        else return;

        window.dataLayer = window.dataLayer || [];
        const payload = { event: eventName };
        window.dataLayer.push(payload);
        console.log(`dataLayer push -> ${eventName}:`, payload, { title: titleRaw });
    }

    bindStickyAtcCapture() {
        // Capture phase so we still see the click even if other code stops propagation.
        document.addEventListener("click", (event) => {
            const button = event.target.closest(".ProductActions.is-sticky ");
            if (!button) return;

            console.log("ATC click captured", button);

            window.dataLayer = window.dataLayer || [];
            const payload = { event: "product_sticky_add_to_cart" };
            window.dataLayer.push(payload);
            console.log("dataLayer push -> product_sticky_add_to_cart:", payload);
        }, true);
    }

    handleShowMoreToggleClick(event) {
        const button = event.target.closest('button[data-action="show-more"]');
        if (!button) return;

        const wrapper = button.closest(".main-product__show-more");
        if (!wrapper) return;

        // The theme toggles `aria-expanded` synchronously; however handler ordering can vary.
        // Read state on next frame so we always observe the post-toggle state.
        requestAnimationFrame(() => {
            const content = wrapper.querySelector(".main-product__show-more-content");
            const expanded = content?.getAttribute("aria-expanded") === "true";

            const eventName = expanded
                ? "product_description_show-more"
                : "product_description_show-less";

            window.dataLayer = window.dataLayer || [];
            const payload = { event: eventName };
            window.dataLayer.push(payload);
            console.log(`dataLayer push -> ${eventName}:`, payload);
        });
    }

    handleProductImageZoomTargetClick(event) {
        const target = event.target.closest(".swiper-zoom-target");
        if (!target) return;

        const width = window.innerWidth || document.documentElement.clientWidth || 0;
        const eventName = width > 1025 ? "product_image_zoom_desktop" : "product_image_click_mobile";

        window.dataLayer = window.dataLayer || [];
        const payload = { event: eventName };
        window.dataLayer.push(payload);

        console.log(`dataLayer push -> ${eventName}:`, payload);
    }

    bindSubscriptionDropdownTracking() {
        // Close/uncheck is dispatched from theme logic and bubbles.
        document.addEventListener("subscription:dropdown:close", () => {
            window.dataLayer = window.dataLayer || [];
            const payload = { event: "product_subscribe_uncheck-box" };
            window.dataLayer.push(payload);

            console.log("dataLayer push -> product_subscribe_uncheck-box:", payload);
        });

        // Keep a simple "check" on click (open) using current state.
        this.delegateBody.on(
            "click",
            '.subscription-dropdown__dropdown[data-action="toggle-sub-dropdown"]',
            (event) => {
                // Don't treat "learn more" clicks as subscribe toggles.
                if (event.target.closest('[data-action="open-sub-drawer"]')) return;

                const dropdown = event.target.closest('.subscription-dropdown__dropdown[data-action="toggle-sub-dropdown"]');
                if (!dropdown) return;
                if (dropdown.classList.contains("is-open")) return;

                window.dataLayer = window.dataLayer || [];
                const payload = { event: "product_subscribe_check-box" };
                window.dataLayer.push(payload);
                console.log("dataLayer push -> product_subscribe_check-box:", payload);
            }
        );

        // "Learn more" click inside subscribe module.
        this.delegateBody.on("click", '[data-action="open-sub-drawer"]', () => {
            window.dataLayer = window.dataLayer || [];
            const payload = { event: "product_subscribe_learn-more" };
            window.dataLayer.push(payload);
            console.log("dataLayer push -> product_subscribe_learn-more:", payload);
        });
    }

    observeDOMChanges() {
        this._pendingIntersectNodes = [];
        this._moScheduled = false;

        this._mutationObserver = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== Node.ELEMENT_NODE) continue;
                    this._pendingIntersectNodes.push(node);
                }
            }

            if (this._moScheduled || !this._pendingIntersectNodes.length) return;
            this._moScheduled = true;
            requestAnimationFrame(() => {
                this._moScheduled = false;
                const roots = this._pendingIntersectNodes;
                this._pendingIntersectNodes = [];
                this.handleIntersects(roots);
            });
        });

        this._mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false,
        });
    }

    getIntersectionObserver() {
        if (this._intersectionObserver) return this._intersectionObserver;

        this._intersectionObserver = new IntersectionObserver(
            this.handleIntersection.bind(this),
            {
                root: null,
                rootMargin: "0px",
                threshold: 0.1,
            }
        );

        return this._intersectionObserver;
    }

    collectIntersectTargets(root) {
        const targets = [];
        if (!root || root.nodeType !== Node.ELEMENT_NODE) return targets;

        if (
            root.matches?.('[data-gtag-intersection][data-observed="false"]')
        ) {
            targets.push(root);
        }

        root
            .querySelectorAll?.('[data-gtag-intersection][data-observed="false"]')
            .forEach((el) => targets.push(el));

        return targets;
    }

    handleIntersects(roots) {
        const observer = this.getIntersectionObserver();
        const scanRoots = roots && roots.length ? roots : [document.body];
        const seen = new Set();

        scanRoots.forEach((root) => {
            this.collectIntersectTargets(root).forEach((item) => {
                if (seen.has(item) || item.dataset.observed === "true") return;
                seen.add(item);
                item.dataset.observed = "true";
                observer.observe(item);
            });
        });
    }

    handleIntersection(entries) {
        const observer = this.getIntersectionObserver();

        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            observer.unobserve(entry.target);

            let data = false;
            try {
                data = JSON.parse(
                    entry.target.querySelector('script[type="application/ld+json"]')
                        .textContent
                );
            } catch (e) {
                console.log("JSON parse failed", e);
            }

            if (!data) return;

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push(data);
            console.log(data, "And pushed to dataLayer");
        });
    }

    // check if okay
    // how we fetch current variant can vary depending on the theme
    handleVariantChange(event) {        
        // get current variant id from url
        const params = new URLSearchParams(window.location.search);
        const variantIdParam = params.get("variant");
    
        if (!variantIdParam) return;
        const currentVariantID = Number(variantIdParam);
    
        // find current variant json in ShopifyAnalytics
        const variants = ShopifyAnalytics.meta.product.variants;
        let currentVariant = null;
    
        variants.forEach(variant => {        
            if (variant.id === currentVariantID) currentVariant = variant;
        })
    
        // get and update gtm script
        const dataElem = document.querySelector('.main-product__gtm-script');    
        if (!currentVariant || !dataElem) return;
        
        const data = JSON.parse(dataElem.textContent);
        const item = data.ecommerce.items[0];
        
        item.item_variant = currentVariant.public_title || "Default Title";
        item.item_id = currentVariant.id || "";
        item.item_sku = currentVariant.sku || "";
        item.price = currentVariant.price / 100;
        item.quantity = 1;
    
        data.ecommerce.value = currentVariant.price / 100;
    
        if (!data) return;
    
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(data);
        console.log(data, "And pushed to dataLayer");
    }

    interceptCartApi() {
        const originalFetch = window.fetch;
        const _this = this;

        window.fetch = async function (...args) {
            const input = args[0];
            const init = args[1];

            // fetch() can be called as fetch(url, init) OR fetch(Request) OR fetch(Request, init)
            const url =
                typeof input === "string"
                    ? input
                    : (input && typeof input.url === "string" ? input.url : "");

            // Don't interfere with non-cart requests (prevents noisy logs from third-party failures).
            if (!url || !url.includes("/cart/")) return originalFetch.apply(this, args);

            // Let fetch failures propagate naturally for cart requests too.
            const response = await originalFetch.apply(this, args);
            if (!response.ok) return response;

            // Trigger custom logic for cart clear/update based on request URL/body.
            if (url.includes(`/cart/clear`)) _this.cartClear();
            if (url.includes(`/cart/update`)) _this.cartUpdate(init?.body);

            // Only GTM-track events for relevant cart actions; never break fetch if parsing fails.
            try {
                window.dataLayer = window.dataLayer || [];

                if (url.includes(`/cart/add`)) {
                    const responseClone = response.clone();
                    const json = await responseClone.json();
                    _this.cartAdd(json, { body: init?.body });
                } else if (url.includes(`/cart/change`)) {
                    const responseClone = response.clone();
                    const json = await responseClone.json();
                    _this.cartChange(json);
                }
            } catch (_) {
                // Intentionally swallow tracking/parsing errors.
            }

            return response;
        };
    }

    // check if quantity is okay
    // when adding product that already exist in a cart fetch response thats returned does not have right product quantity
    // instead of showing how many products has been added it shows how many there are in cart
    async cartAdd(cart, request) {
        let cartItems = cart.items ? cart.items : [cart];
        cartItems = this.getDataFromRequest(cartItems, request);
        this.productAdded(cartItems);
    }

    async cartUpdate(body) {
        if (!body) return;
        const updates = JSON.parse(body).updates;
    
        const oldCart = await this.getCart();
        const cartItems = oldCart.items, itemsAdded = [], itemsRemoved = [];
    
        cartItems.forEach(item => {            
            const newQuantity = updates[item.key];
            if (typeof newQuantity === "number") {
                const change = newQuantity - item.quantity;
                item.quantity = Math.abs(change);
    
                change < 1 ? itemsRemoved.push(item) : itemsAdded.push(item);
            }
        })
    
        this.productAdded(itemsAdded);
        this.productsRemoved(itemsRemoved);
    }

    async cartChange(cart) {
        this.productAdded(cart.items_added);
        this.productsRemoved(cart.items_removed);
    }

    async cartClear() {
        const cart = await this.getCart();
        this.productsRemoved(cart.items);
    }

    async getCart() {
        try {
            const response = await fetch(window.routes.localeRootUrl + "/cart.js", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });

            return await response.json();

        } catch (error) {
            console.log('Fetch Error:', error);
        }
    }

    productAdded(cartItems) {
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
            'event': 'add_to_cart',
            'ecommerce': this.buildEcommerce(cartItems)
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
        console.log("dataLayer push -> add_to_cart:", customEvent);
    }

    async productsRemoved(cartItems) {
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
              'event': "custom_remove_from_cart",
              'ecommerce': this.buildEcommerce(cartItems)
        }
  
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
        console.log("dataLayer push -> custom_remove_from_cart:", customEvent);
    }

    handleCheckout() {
        const cartItems = CartDrawer.cart?.items;
        if (!cartItems || !cartItems.length) return;

        const customEvent = {
            'event': 'begin_checkout',
            'ecommerce': this.buildEcommerce(cartItems)
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
    }

    handleSelectItem(event) {
        let target = event.target.closest('[data-action="ga-send-select"]');
        if (!target) return;

        const settings = JSON.parse(target.dataset.selectSettings);
        if (!settings) return;

        const customEvent = {
            event: "custom_select_item",
            ecommerce: {
                selected_item_list_id: settings.list_id,
                selected_item_list_name: settings.list_name,
                currency: ShopifyAnalytics.meta.currency,
                items: [settings]
            }
        }
    
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(customEvent);
        console.log("dataLayer push -> custom_select_item:", customEvent);
    }

    buildEcommerce(cartItems) {
        const items = [];
        let totalPrice = 0;        

        cartItems.forEach((item) => {            
            const price = typeof item.price === "number" ? item.price / 100 : Number(item.price);
            totalPrice += price * item.quantity;
            
            const product = {
                'item_id': item.sku || "",
                'item_name': item.untranslated_product_title || item.title || "",
                'item_variant': item.untranslated_variant_title || "Default Title",
                'price': price, 
                'quantity': item.quantity,
                'category': item.properties?.["_gtm-category"] || "",
                'category2': item.properties?.["_gtm-category2"] || "",
                'item_brand': item.properties?.["_gtm-item_brand"] || "",
                'list_id': item.properties?.["_gtm-list_id"] || "",
                'list_name': item.properties?.["_gtm-list_name"] || ""
            }

            items.push(product);
        });

        return {
            'value': totalPrice,
            'currency': ShopifyAnalytics.meta.currency,
            'items': items
        }
    }

    getDataFromRequest(cartItems, request) {
        const body = request?.body || null;
        if (!body) return cartItems;

        let quantity = null;

        // Body can be FormData/URLSearchParams (has get), or a JSON string (theme uses JSON for /cart/add.js)
        if (typeof body === "string") {
            try {
                const parsed = JSON.parse(body);
                if (parsed && parsed.quantity != null) quantity = parsed.quantity;
            } catch (_) {
                // ignore
            }
        } else if (typeof body?.get === "function") {
            quantity = body.get("quantity");
        }

        if (quantity != null && cartItems?.[0]) cartItems[0].quantity = Number(quantity);

        return cartItems;
    }

    /**
     * Whether an event counts as the shopper activating the element it fired on.
     *
     * Anything that is not a keydown - a click, a change - is already an activation by the time it
     * reaches a handler. A keydown is only one for the two keys the theme itself acts on, so a
     * shopper tabbing or arrowing through the filter panel raises nothing.
     *
     * @param {Event} event - The delegated event.
     * @returns {boolean} True when the event should be reported.
     */
    isGtmActivation(event) {
        return event.type !== "keydown" || event.key === "Enter" || event.key === " ";
    }

    /**
     * Pushes one event onto the GTM dataLayer, creating it if this is the first push.
     *
     * @param {{event: string}} payload - The dataLayer payload; `event` is the GTM event name.
     * @returns {void}
     */
    pushDataLayerEvent(payload) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
        console.log(`dataLayer push -> ${payload.event}:`, payload);
    }

    /**
     * Reads the currently selected filters out of the filter form.
     *
     * Each group is found by the data-gtm-filter-name the Liquid puts on it, which carries the
     * merchant-facing label. Checkbox groups report one entry per checked value; a price range
     * reports a single "min-max" entry, and only once the shopper has actually narrowed it, so an
     * untouched range is not mistaken for an active filter.
     *
     * @param {HTMLFormElement} form - The collection filters form.
     * @returns {Array<{name: string, value: string}>} Active filters, in DOM order.
     */
    getActiveCollectionFilters(form) {
        const filters = [];

        form.querySelectorAll("[data-gtm-filter-name]").forEach((group) => {
            const name = this.normalizeGtmText(group.dataset.gtmFilterName);

            const checked = Array.from(group.querySelectorAll(".inputFilterCheckbox:checked"))
                .map((input) => this.normalizeGtmText(input.value))
                .filter(Boolean);

            if (checked.length) {
                checked.forEach((value) => filters.push({ name, value }));
                return;
            }

            // The range slider writes formatted values (e.g. "0.00 kr") into both inputs
            // the moment noUiSlider initialises, so a filled-in input does not mean the
            // user narrowed anything. Compare against the slider's own bounds instead.
            const min = group.querySelector("[data-custom-min]");
            const max = group.querySelector("[data-custom-max]");
            if (!min && !max) return;

            const floor = this.parseGtmNumber(min?.getAttribute("min")) ?? 0;
            const ceiling = this.parseGtmNumber(max?.getAttribute("max"));
            const from = this.parseGtmNumber(min?.value);
            const to = this.parseGtmNumber(max?.value);

            const narrowed =
                (from !== null && from > floor) ||
                (to !== null && ceiling !== null && to < ceiling);
            if (!narrowed) return;

            filters.push({ name, value: `${from ?? floor}-${to ?? ceiling}` });
        });

        return filters;
    }

    /**
     * Collapses the whitespace in a value read out of the DOM so filter and sort names arrive in
     * the dataLayer as one clean line, whatever indentation the Liquid left in them.
     *
     * @param {string|null|undefined} value - The raw value.
     * @returns {string} The normalised value, or "" when there was none.
     */
    normalizeGtmText(value) {
        return (value || "").replace(/\s+/g, " ").trim();
    }

    /**
     * Reads a number out of a money-formatted price input, so the price range can be compared
     * against the slider's bounds. Separators vary by currency ("1,234.50 $", "1.234,50 kr"),
     * so whichever separator appears last is treated as the decimal one.
     *
     * @param {string|null|undefined} value - The raw value.
     * @returns {number|null} The parsed number, or null when there was none to read.
     */
    parseGtmNumber(value) {
        if (value == null) return null;

        const digits = String(value).replace(/[^\d.,]/g, "");
        if (!digits) return null;

        const normalized = digits.lastIndexOf(",") > digits.lastIndexOf(".")
            ? digits.replace(/\./g, "").replace(",", ".")
            : digits.replace(/,/g, "");

        const number = Number.parseFloat(normalized);
        return Number.isFinite(number) ? number : null;
    }
}

new gTagEvents();